// Tasarımcı geometri yardımcıları — saf, test edilebilir (DOM bağımsız).
export const PX_PER_MM = 3.7795; // 96 dpi

export const SNAP_PX = 6; // ekranda ~6px snap hissi (zoom-bağımsız)

export function round(v) { return Math.round(v * 10) / 10; }

export function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

// Sürüklenen elemanın snap'leneceği hedef çizgileri toplar (mm).
// Dönen: { x: [{pos, kind}], y: [{pos, kind}] } — kind sadece teşhis/öncelik için.
export function collectSnapTargets(layout, dims, excludeIds) {
  const m = layout.page.margins || { top: 0, right: 0, bottom: 0, left: 0 };
  const exclude = new Set(excludeIds || []);
  const W = dims.widthMm;
  const H = dims.heightMm;
  // Yazdırılabilir alan koordinatları printable-area-relative (Rnd parent = printable alan).
  const pw = W - m.left - m.right;
  const ph = H - m.top - m.bottom;

  const x = [
    { pos: 0, kind: 'edge' },              // sol kenar (printable)
    { pos: pw, kind: 'edge' },             // sağ kenar
    { pos: pw / 2, kind: 'center' },       // yatay merkez
  ];
  const y = [
    { pos: 0, kind: 'edge' },
    { pos: ph, kind: 'edge' },
    { pos: ph / 2, kind: 'center' },
  ];

  for (const el of layout.elements || []) {
    if (exclude.has(el.id) || el.hidden) continue;
    const w = el.width || 0;
    const h = el.height || 0;
    x.push({ pos: el.x, kind: 'el' }, { pos: el.x + w / 2, kind: 'el' }, { pos: el.x + w, kind: 'el' });
    y.push({ pos: el.y, kind: 'el' }, { pos: el.y + h / 2, kind: 'el' }, { pos: el.y + h, kind: 'el' });
  }
  // Detay bandının kenarları da hedef.
  const band = layout.detailBand;
  if (band && band.enabled) {
    x.push({ pos: band.x, kind: 'band' }, { pos: band.x + (band.width || 0), kind: 'band' });
    y.push({ pos: band.y, kind: 'band' });
  }
  return { x, y };
}

// Taşınan kutuyu (mm) hedeflere yapıştırır. moving = {x, y, w, h}.
// Dönen: { x, y, snappedX, snappedY, guides:[{axis, pos, a, b}] } (pos/a/b printable-relative mm).
export function computeSnap(moving, targets, tolMm) {
  const { x, y, w, h } = moving;
  let outX = x, outY = y, snappedX = false, snappedY = false;
  const guides = [];

  // X ekseni: elemanın sol/merkez/sağ anahtarlarını hedeflerle karşılaştır.
  const xKeys = [{ key: x, off: 0 }, { key: x + w / 2, off: w / 2 }, { key: x + w, off: w }];
  let bestX = null;
  for (const t of targets.x) {
    for (const k of xKeys) {
      const d = Math.abs(k.key - t.pos);
      if (d <= tolMm && (!bestX || d < bestX.d)) bestX = { d, pos: t.pos, off: k.off };
    }
  }
  if (bestX) { outX = round(bestX.pos - bestX.off); snappedX = true; guides.push({ axis: 'x', pos: bestX.pos }); }

  const yKeys = [{ key: y, off: 0 }, { key: y + h / 2, off: h / 2 }, { key: y + h, off: h }];
  let bestY = null;
  for (const t of targets.y) {
    for (const k of yKeys) {
      const d = Math.abs(k.key - t.pos);
      if (d <= tolMm && (!bestY || d < bestY.d)) bestY = { d, pos: t.pos, off: k.off };
    }
  }
  if (bestY) { outY = round(bestY.pos - bestY.off); snappedY = true; guides.push({ axis: 'y', pos: bestY.pos }); }

  return { x: outX, y: outY, snappedX, snappedY, snapped: snappedX || snappedY, guides };
}

// Resize sırasında: hareket eden kenarı (sağ/alt için x+w, y+h) hedeflere yapıştır.
// edge = {right:bool, bottom:bool, left:bool, top:bool}. Dönen yeni {x,y,w,h, guides, snapped}.
export function computeResizeSnap(box, edge, targets, tolMm) {
  let { x, y, w, h } = box;
  const guides = [];
  let snapped = false;
  const snap1D = (val, list) => {
    let best = null;
    for (const t of list) { const d = Math.abs(val - t.pos); if (d <= tolMm && (!best || d < best.d)) best = { d, pos: t.pos }; }
    return best;
  };
  if (edge.right) { const b = snap1D(x + w, targets.x); if (b) { w = round(b.pos - x); snapped = true; guides.push({ axis: 'x', pos: b.pos }); } }
  if (edge.left) { const b = snap1D(x, targets.x); if (b) { w = round(w + (x - b.pos)); x = round(b.pos); snapped = true; guides.push({ axis: 'x', pos: b.pos }); } }
  if (edge.bottom) { const b = snap1D(y + h, targets.y); if (b) { h = round(b.pos - y); snapped = true; guides.push({ axis: 'y', pos: b.pos }); } }
  if (edge.top) { const b = snap1D(y, targets.y); if (b) { h = round(h + (y - b.pos)); y = round(b.pos); snapped = true; guides.push({ axis: 'y', pos: b.pos }); } }
  return { x, y, w, h, guides, snapped };
}

// Grid'e yuvarla (snap-to-grid fallback).
export function snapToGrid(v, gridMm) {
  if (!gridMm) return round(v);
  return round(Math.round(v / gridMm) * gridMm);
}
